import { LocalStorage } from 'quasar'
import { LOCAL_STORAGE_KEYS } from 'src/boot/constants'
import { useClientContext } from 'src/composables/useClientContext'
import { computed, watch } from 'vue'

const PROTECT_SENSITIVE_DATA_CLASS = 'protect-sensitive-data'
let wasDemoModeAtBoot = false
let hasSetDemoModeAtBoot = false

/**
 * Initializes and returns the current `isDemoMode` state as a writable computed property.
 * For wide accessibility, this value is stored in `Model.context.demoMode`. We also persist
 * the `isDemoMode` state in localStorage to persist across page refreshes. The Model.context
 * is awaited.
 *
 * The returned `isDemoMode` ref value can be managed using v-model binding, or accessed directly.
 * @example
 * ```vue
 * <template>
 *   <q-toggle v-model="isDemoMode" />
 * </template>
 * ```
 * @example
 * ```js
 * const { isDemoMode } = useIsDemoMode()
 * isDemoMode.value = true
 * console.log(isDemoMode.value)
 * ```
 */
export default function useIsDemoMode () {
  const { clientContext, isLoaded, ensureBooted } = useClientContext()

  if (!hasSetDemoModeAtBoot) {
    wasDemoModeAtBoot = LocalStorage.getItem(LOCAL_STORAGE_KEYS.isDemoMode) || false
    if (wasDemoModeAtBoot) {
      document.body.classList.add(PROTECT_SENSITIVE_DATA_CLASS)
    }
    ensureBooted().then(() => {
      clientContext.value.demoMode = wasDemoModeAtBoot
    })
    hasSetDemoModeAtBoot = true
  }

  const isDemoMode = computed({
    /** @returns {boolean} */
    get () {
      return isLoaded.value ? clientContext.value.demoMode : wasDemoModeAtBoot
    },
    /** @param {boolean} value */
    set (value) {
      if (isLoaded.value) {
        clientContext.value.demoMode = Boolean(value)
      }
    }
  })

  watch(isDemoMode, (newValue) => {
    if (newValue) {
      document.body.classList.add(PROTECT_SENSITIVE_DATA_CLASS)
      LocalStorage.setItem(LOCAL_STORAGE_KEYS.isDemoMode, true)
    } else {
      document.body.classList.remove(PROTECT_SENSITIVE_DATA_CLASS)
      LocalStorage.removeItem(LOCAL_STORAGE_KEYS.isDemoMode)
    }
  })

  return {
    wasDemoMode: wasDemoModeAtBoot,
    isDemoMode
  }
}
